import React, { useState } from 'react';
import { 
  FaShieldAlt, 
  FaUserLock, 
  FaChartBar, 
  FaHeadset,
  FaSearch,
  FaCheck,
  FaGlobe,
  FaLock,
  FaCogs,
  FaUsers,
  FaVideo,
  FaRegLightbulb,
  FaServer,
  FaRegClipboard,
  FaArrowRight
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Enterprise = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(1470);

  React.useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await response.json();
        if (data && data.rates && data.rates.KRW) {
          setExchangeRate(Math.round(data.rates.KRW));
        }
      } catch (error) {
        console.error("Exchange rate fetch error:", error);
      }
    };
    fetchExchangeRate();
  }, []);

  const formatKRW = (usdPrice) => {
    const krw = Math.round(parseFloat(usdPrice) * exchangeRate);
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(krw);
  };

  
  const features = [
    {
      title: '엔터프라이즈급 보안',
      icon: <FaLock className="text-indigo-600 w-8 h-8 mb-4" />,
      description: '기업의 중요 데이터를 완벽하게 보호하는 최고급 보안 솔루션',
      items: [
        {text: 'DLP(데이터 유출 방지)', detail: '민감한 정보의 외부 유출 차단'},
        {text: 'S/MIME 암호화', detail: '이메일 콘텐츠 암호화로 기밀 유지'},
        {text: '고급 엔드포인트 관리', detail: '모든 기기의 통합 보안 관리'},
        {text: '컨텍스트 인식 접근 제어', detail: '상황별 접근 권한 조정'},
        {text: 'Vault for eDiscovery', detail: '데이터 보관 및 법적 증거 수집'},
        {text: '보안 관리 대시보드', detail: '실시간 보안 모니터링'}
      ]
    },
    {
      title: '고급 협업 기능',
      icon: <FaUsers className="text-blue-600 w-8 h-8 mb-4" />,
      description: '대규모 조직의 효율적인 협업을 위한 확장 가능한 도구',
      items: [
        {text: '화상 회의 최대 500명 참석', detail: '대규모 화상 회의 지원'},
        {text: '회의 녹화 및 저장', detail: '클라우드에 자동 저장'},
        {text: '실시간 자막 및 번역', detail: '다국어 실시간 번역 지원'},
        {text: '노이즈 캔슬링', detail: 'AI 기반 배경 소음 제거'},
        {text: '출결 관리 기능', detail: '회의 참석자 자동 추적'},
        {text: '고급 모더레이션 도구', detail: '회의 진행 관리 도구'}
      ]
    },
    {
      title: '관리자 기능',
      icon: <FaCogs className="text-purple-600 w-8 h-8 mb-4" />,
      description: 'IT 관리자를 위한 강력한 제어 및 분석 도구',
      items: [
        {text: '데이터 통합 분석', detail: '사용량 및 생산성 패턴 분석'},
        {text: '고급 감사 로그', detail: '모든 활동에 대한 상세 기록'},
        {text: '커스텀 알림 설정', detail: '중요 이벤트에 대한 맞춤 알림'},
        {text: 'API 접근 제어', detail: '서드파티 앱 통합 관리'},
        {text: '고급 보고서 생성', detail: '맞춤형 보고서 자동화'},
        {text: '멀티 도메인 관리', detail: '여러 도메인의 통합 관리'}
      ]
    }
  ];

  const benefits = [
    {
      icon: <FaShieldAlt />,
      title: '최고 수준의 보안',
      description: '엔터프라이즈급 보안으로 데이터 침해 위험 85% 감소'
    },
    {
      icon: <FaGlobe />,
      title: '글로벌 규정 준수',
      description: 'GDPR, HIPAA 등 국제 규정 준수 지원'
    },
    {
      icon: <FaRegLightbulb />,
      title: '생산성 혁신',
      description: 'AI 기반 도구로 업무 효율성 40% 향상'
    },
    {
      icon: <FaServer />,
      title: '무제한 스토리지',
      description: '사용자당 무제한 클라우드 스토리지 제공'
    }
  ];


return (
  <div className="bg-gradient-to-b from-gray-50 via-indigo-50 to-white py-20 px-4 sm:px-6 lg:px-8">
    {/* 헤더 섹션 */}
    <div className="max-w-7xl mx-auto text-center mb-20">
      <div className="inline-block bg-indigo-100 text-indigo-700 px-4 py-1 rounded-full font-medium text-sm mb-4 border border-indigo-200">
        GOOGLE WORKSPACE
      </div>
      <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-8">
        Enterprise
      </h1>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
        대기업을 위한 최고급 보안 및 관리 기능을 갖춘 엔터프라이즈 솔루션
      </p>
      
      {/* 핵심 혜택 배지 */}
      <div className="flex flex-wrap justify-center gap-4 mt-10">
        {['최고급 보안', '무제한 스토리지', '500명 화상회의', '24/7 프리미엄 지원'].map((badge, index) => (
          <span key={index} className="bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium border border-indigo-200">
            {badge}
          </span>
        ))}
      </div>
      <div className="mt-8 flex justify-center">
        <p className="text-indigo-700 text-sm font-medium bg-indigo-50 px-4 py-1.5 rounded-full border border-indigo-100 flex items-center">
          <FaUsers className="mr-2" /> 1년 약정 및 10명 이상 계약 시 기업 맞춤 할인 혜택이 적용됩니다
        </p>
      </div>
    </div>

    {/* 2열 레이아웃: 견적 + 주요 장점 */}
    <div className="max-w-7xl mx-auto mb-24 grid md:grid-cols-2 gap-12 items-center">
      {/* 견적 섹션 */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl border border-indigo-100">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-5 border-b">
          <div className="text-xl font-bold text-white">Enterprise 맞춤 견적</div>
        </div>
        <div className="p-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xl text-gray-800">맞춤형 가격 제안</div>
              <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-medium">
                기업 맞춤형
              </span>
            </div>
            <p className="text-gray-600 mb-4">
              귀사의 규모와 요구사항에 맞는 최적의 가격 제안
            </p>
            
            <div className="space-y-3 mb-6 bg-indigo-50 rounded-xl p-4 border border-indigo-100">
              <div className="flex items-center text-gray-700">
                <FaCheck className="text-indigo-600 mr-3 flex-shrink-0" />
                <span>사용자 수에 따른 할인 혜택</span>
              </div>
              <div className="flex items-center text-gray-700">
                <FaCheck className="text-indigo-600 mr-3 flex-shrink-0" />
                <span>장기 계약 시 추가 할인</span>
              </div>
              <div className="flex items-center text-gray-700">
                <FaCheck className="text-indigo-600 mr-3 flex-shrink-0" />
                <span>프리미엄 기술 지원 포함</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <a 
              href="http://pf.kakao.com/_jyebn/chat" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full"
            >
              <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md flex items-center justify-center">
                <span>맞춤 견적 및 상담 요청하기</span>
                <FaArrowRight className="ml-2" />
              </button>
            </a>
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">
            평균 응답 시간: 24시간 이내
          </p>
        </div>
      </div>
      
      {/* 주요 장점 섹션 */}
      <div className="rounded-2xl p-8 bg-white shadow-xl border border-indigo-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-8">
          Enterprise 도입 효과
        </h3>
        <div className="space-y-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex group">
              <div className="text-indigo-600 text-xl mt-1 mr-4 transition-all duration-300 group-hover:text-indigo-500 transform group-hover:scale-110">
                {benefit.icon}
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-indigo-700 transition-colors duration-300">
                  {benefit.title}
                </h4>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* 특징 섹션 - 탭 스타일 */}
    <div className="max-w-7xl mx-auto mb-24">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
        엔터프라이즈급 기능
      </h2>
      
      {/* 탭 네비게이션 */}
      <div className="flex flex-wrap justify-center mb-10 gap-2 px-2">
        {features.map((feature, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={`px-5 py-3 rounded-full text-sm sm:text-base font-medium transition-all duration-300 ${
              activeTab === idx 
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {feature.title}
          </button>
        ))}
      </div>
      
      {/* 탭 콘텐츠 */}
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-indigo-100">
        <div className="flex items-center mb-8">
          {features[activeTab].icon}
          <div className="ml-4">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {features[activeTab].title}
            </h3>
            <p className="text-gray-600">
              {features[activeTab].description}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features[activeTab].items.map((item, idx) => (
            <div key={idx} className="flex items-start bg-indigo-50 p-4 rounded-xl border border-indigo-100 hover:border-indigo-300 hover:shadow-md transition-all duration-300">
              <FaCheck className="w-5 h-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <div className="text-gray-800 font-medium">{item.text}</div>
                <div className="text-gray-600 text-sm mt-1">{item.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* 기업 고객 활용 사례 */}
    <div className="max-w-7xl mx-auto mb-24">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
        국내 대기업 활용 사례
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          {
            industry: '금융',
            company: '대형 금융 그룹',
            result: '보안 강화 및 규정 준수',
            detail: '엄격한 금융 규제를 준수하면서 12,000명 이상의 직원들에게 안전한 협업 환경 제공'
          },
          {
            industry: '제조',
            company: '글로벌 제조 기업',
            result: '글로벌 커뮤니케이션 개선',
            detail: '15개국 지사와의 원활한 소통으로 제품 개발 주기 20% 단축 및 운영 비용 절감'
          },
          {
            industry: '서비스',
            company: '대형 유통 기업',
            result: '비즈니스 민첩성 향상',
            detail: '전국 250개 지점의 실시간 데이터 공유로 의사결정 속도 향상 및 고객 대응력 강화'
          }
        ].map((case_, index) => (
          <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-indigo-100 hover:shadow-xl transition-all duration-300">
            <div className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-medium inline-block mb-4">
              {case_.industry} 산업
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">
              {case_.company}
            </h3>
            <div className="text-indigo-700 font-semibold mb-2">
              {case_.result}
            </div>
            <p className="text-gray-600 text-sm">
              {case_.detail}
            </p>
          </div>
        ))}
      </div>
    </div>

    {/* 비교 표 */}
    <div className="max-w-7xl mx-auto mb-24">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
        플랜 비교
      </h2>
      
      <div className="overflow-x-auto">
        <table className="w-full bg-white rounded-xl shadow-xl border border-indigo-100">
          <thead>
            <tr className="border-b border-indigo-100">
              <th className="py-6 px-6 text-left text-gray-700 font-medium">기능</th>
              <th className="py-6 px-6 text-center text-gray-700 font-medium">Business Standard</th>
              <th className="py-6 px-6 text-center text-indigo-700 font-bold bg-indigo-50">
                <span className="inline-block px-2 py-1 rounded-full bg-indigo-600 text-white text-xs mb-1">프리미엄</span>
                <div>Enterprise</div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-indigo-100">
            <tr>
              <td className="py-5 px-6 text-gray-700">스토리지</td>
              <td className="py-5 px-6 text-center text-gray-600">2TB / 사용자</td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">무제한</td>
            </tr>
            <tr>
              <td className="py-5 px-6 text-gray-700">화상회의</td>
              <td className="py-5 px-6 text-center text-gray-600">최대 150명</td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">최대 500명</td>
            </tr>
            <tr>
              <td className="py-5 px-6 text-gray-700">보안 기능</td>
              <td className="py-5 px-6 text-center text-gray-600">고급</td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">엔터프라이즈급</td>
            </tr>
            <tr>
              <td className="py-5 px-6 text-gray-700">데이터 유출 방지</td>
              <td className="py-5 px-6 text-center text-gray-600">
                <FaCheck className="w-5 h-5 text-red-500 mx-auto" />
              </td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">
                <FaCheck className="w-5 h-5 text-green-500 mx-auto" />
              </td>
            </tr>
            <tr>
              <td className="py-5 px-6 text-gray-700">S/MIME 암호화</td>
              <td className="py-5 px-6 text-center text-gray-600">
                <FaCheck className="w-5 h-5 text-red-500 mx-auto" />
              </td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">
                <FaCheck className="w-5 h-5 text-green-500 mx-auto" />
              </td>
            </tr>
            <tr>
              <td className="py-5 px-6 text-gray-700">고급 분석</td>
              <td className="py-5 px-6 text-center text-gray-600">제한적</td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">포괄적</td>
            </tr>
            <tr>
              <td className="py-5 px-6 text-gray-700">가격 (월/사용자)</td>
              <td className="py-5 px-6 text-center text-gray-600">
                <div>$12.60 USD</div>
                <div className="text-[10px] text-blue-600 font-bold">({formatKRW(12.60)})</div>
              </td>
              <td className="py-5 px-6 text-center font-medium text-indigo-700 bg-indigo-50">상담 문의</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    {/* 전문가 컨설팅 섹션 */}
    <div className="max-w-5xl mx-auto mt-24 mb-8 text-center">
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-12 shadow-xl text-white overflow-hidden relative">
        {/* 배경 장식 */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-indigo-300"></div>
        </div>
        
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-6 text-white">
            엔터프라이즈 전문가의 맞춤 컨설팅
          </h2>
          <p className="text-xl opacity-90 mb-10 w-full mx-auto text-white">
            기업 환경에 최적화된 Google Workspace Enterprise 솔루션을 구축해 드립니다
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="http://pf.kakao.com/_jyebn/chat" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <button className="bg-white text-indigo-700 px-12 py-4 rounded-lg font-bold hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center">
                맞춤 견적 및 컨설팅 요청
                <FaArrowRight className="ml-2" />
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>

    {/* 보안 인증 배지 */}
    <div className="max-w-4xl mx-auto text-center mb-16">
      <p className="text-gray-600 mb-4">국제 보안 인증 및 규정 준수</p>
      <div className="flex flex-wrap justify-center gap-6">
        {['ISO 27001', 'GDPR', 'HIPAA', 'SOC 2/3', 'FedRAMP'].map((cert, index) => (
          <div key={index} className="bg-white px-5 py-2 rounded-lg border border-indigo-200 text-gray-700 text-sm shadow-sm">
            {cert}
          </div>
        ))}
      </div>
    </div>
  </div>
);
};

export default Enterprise;